import React from 'react';
import { Box } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { AdvisorySourceQuote } from './advisoryProvenance';
import { quoteLabelColor } from './AdvisoryQuoteLabel';

export type EvidenceTone = 'success' | 'warning' | 'error';

export const quoteCheckTone = (status: AdvisorySourceQuote['status']): EvidenceTone =>
  status === 'verified' ? 'success' : status === 'absent' ? 'error' : 'warning';

export const evidenceColors = (theme: Theme, tone: EvidenceTone) => {
  const color = quoteLabelColor(theme, tone);
  return {
    color,
    backgroundColor: alpha(color, 0.08),
    borderColor: alpha(color, 0.3),
  };
};

export function EvidenceStatusIcon({ tone }: { tone: EvidenceTone }) {
  const Icon =
    tone === 'success'
      ? CheckCircleOutlineIcon
      : tone === 'error'
      ? ErrorOutlineIcon
      : WarningAmberIcon;
  return <Icon aria-hidden sx={{ fontSize: 18, flexShrink: 0 }} />;
}

export function AdvisoryEvidenceCallout({
  tone,
  showIcon = true,
  plain = false,
  children,
}: {
  tone: EvidenceTone;
  showIcon?: boolean;
  plain?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Box
      role="note"
      data-evidence-tone={tone}
      sx={theme => ({
        ...(plain
          ? {
              color: theme.colors.inkPrimary,
              borderTop: `1px solid ${theme.visualPrimitives.borderLight}`,
              paddingY: 2,
            }
          : {
              border: '1px solid',
              ...evidenceColors(theme, tone),
              borderLeft: `3px solid ${quoteLabelColor(theme, tone)}`,
              borderRadius: 1,
              padding: 2,
            }),
        marginTop: 1.5,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
      })}
    >
      {showIcon && !plain ? (
        <Box sx={{ display: 'flex', paddingTop: 0.25 }}>
          <EvidenceStatusIcon tone={tone} />
        </Box>
      ) : null}
      <Box sx={{ minWidth: 0, flex: 1 }}>{children}</Box>
    </Box>
  );
}
